import { useState, type FunctionComponent } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Stack,
    SvgIcon,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CheckCircleIcon from '@heroicons/react/24/solid/CheckCircleIcon';
import { usePagination } from '@starjack/shared-components';
import {
    QUESTION_LABEL_MAX_LENGTH,
    useGetAssistantQuestionsQuery,
    useCreateAssistantQuestionMutation,
    useUpdateAssistantQuestionMutation,
    useDeleteAssistantQuestionMutation,
    type PredefinedQuestionRecord,
    type QuestionFunnel,
} from '@starjack/entities/assistantConfig';
import { ASSISTANT_API } from '@starjack/utils';
import {
    StarjackTable,
    type StajackTableColumn,
} from '@/components/Table/v2/Table';
import {
    errorStatus,
    isConnectionError,
    MISCONFIGURED_MESSAGE,
} from '@/pageContents/kiEinstellungen/assistantApiError';

// The stored value stays the English `funnel` name; only the label shown to
// support is German. "funnel" itself must never appear in the UI (spec §3.1)
// — `product` drives a server-authored operator-picker shortcut, so picking
// the right option here matters even though the word behind it doesn't show.
const FUNNEL_OPTIONS: { value: QuestionFunnel; label: string }[] = [
    { value: 'info', label: 'Allgemeine Frage' },
    { value: 'product', label: 'Ticket kaufen' },
    { value: 'booking', label: 'Mein Konto' },
];

const funnelLabel = (funnel: QuestionFunnel): string =>
    FUNNEL_OPTIONS.find((option) => option.value === funnel)?.label ?? funnel;

type QuestionDraft = Omit<PredefinedQuestionRecord, 'id'>;

const emptyDraft = (sortIndex: number): QuestionDraft => ({
    labelDe: '',
    labelEn: '',
    funnel: 'info',
    requiresLogin: false,
    sortIndex,
    enabled: true,
});

const BooleanFlag: FunctionComponent<{ value: boolean }> = ({ value }) =>
    value ? (
        <SvgIcon fontSize='small' color='success'>
            <CheckCircleIcon />
        </SvgIcon>
    ) : null;

export const AssistantQuestionsContent: FunctionComponent = () => {
    const { data, isLoading, error } = useGetAssistantQuestionsQuery(
        undefined,
        { skip: !ASSISTANT_API }
    );
    const [
        createQuestion,
        { isLoading: isCreating, error: createError, reset: resetCreate },
    ] = useCreateAssistantQuestionMutation();
    const [
        updateQuestion,
        { isLoading: isUpdating, error: updateError, reset: resetUpdate },
    ] = useUpdateAssistantQuestionMutation();
    const [
        deleteQuestion,
        { isLoading: isDeleting, error: deleteError, reset: resetDelete },
    ] = useDeleteAssistantQuestionMutation();

    // `editingId` distinguishes the two dialog modes: null means the draft is
    // a new question (POST on save), an id means it is an existing row (PUT).
    const [editingId, setEditingId] = useState<string | null>(null);
    const [draft, setDraft] = useState<QuestionDraft | null>(null);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    // Ordered by sortIndex client-side too — the endpoint already orders
    // this way, but the table must never silently drift from what support
    // configured if that ever changes.
    const rows = [...(data ?? [])].sort((a, b) => a.sortIndex - b.sortIndex);

    const {
        currentItems,
        countTotal,
        page,
        setNextPage,
        itemsPerPage,
        setItemsPerPage,
    } = usePagination({ items: rows, itemsPerPage: 25 });

    if (!ASSISTANT_API)
        return <Alert severity='error'>{MISCONFIGURED_MESSAGE}</Alert>;

    if (isLoading) return <CircularProgress />;
    if (error)
        return (
            <Alert severity='error'>
                {isConnectionError(error)
                    ? 'Assistant nicht erreichbar'
                    : 'Fragen nicht ladbar'}
            </Alert>
        );

    const columns: StajackTableColumn<PredefinedQuestionRecord>[] = [
        { label: 'Frage (DE)', itemKey: 'labelDe' },
        { label: 'Question (EN)', itemKey: 'labelEn' },
        {
            label: 'Art',
            itemTransform: (row) => funnelLabel(row.funnel),
        },
        {
            label: 'Login erforderlich',
            itemTransform: (row) => <BooleanFlag value={row.requiresLogin} />,
            options: {
                headerCellProps: { align: 'center' },
                rowCellProps: { align: 'center' },
            },
        },
        { label: 'Reihenfolge', itemKey: 'sortIndex' },
        {
            label: 'Aktiv',
            itemTransform: (row) => <BooleanFlag value={row.enabled} />,
            options: {
                headerCellProps: { align: 'center' },
                rowCellProps: { align: 'center' },
            },
        },
    ];

    // Clears any previous mutation's leftover result — RTK Query keeps the
    // last outcome until the trigger runs again or is reset, so without this
    // a failed save on one row would render its error on the next dialog
    // before a save was ever attempted there.
    const resetMutations = () => {
        resetCreate();
        resetUpdate();
        resetDelete();
    };

    const openCreate = () => {
        resetMutations();
        setEditingId(null);
        setDraft(emptyDraft(rows.length));
    };

    const openEdit = (row: PredefinedQuestionRecord) => {
        resetMutations();
        setEditingId(row.id);
        setDraft({
            labelDe: row.labelDe,
            labelEn: row.labelEn,
            funnel: row.funnel,
            requiresLogin: row.requiresLogin,
            sortIndex: row.sortIndex,
            enabled: row.enabled,
        });
    };

    const close = () => {
        resetMutations();
        setEditingId(null);
        setDraft(null);
        setConfirmingDelete(false);
    };

    const updateDraft = (patch: Partial<QuestionDraft>) =>
        setDraft((prev) => (prev ? { ...prev, ...patch } : prev));

    const submit = async () => {
        if (!draft) return;
        try {
            if (editingId) {
                await updateQuestion({ id: editingId, ...draft }).unwrap();
            } else {
                await createQuestion(draft).unwrap();
            }
            close();
        } catch {
            // createError / updateError drives the dialog's inline alert;
            // keep the dialog open so the failed edit isn't lost.
        }
    };

    const confirmDelete = async () => {
        if (!editingId) return;
        try {
            await deleteQuestion(editingId).unwrap();
            close();
        } catch {
            // deleteError drives the confirmation dialog's inline alert.
        }
    };

    const saveError = createError ?? updateError;
    const isSaving = isCreating || isUpdating;

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mb: 2,
                }}
            >
                <Button variant='contained' onClick={openCreate}>
                    Neue Frage
                </Button>
            </Box>

            <StarjackTable<PredefinedQuestionRecord>
                allItems={rows}
                count={countTotal}
                currentPage={page}
                items={currentItems}
                onPageChange={setNextPage}
                rowsPerPage={itemsPerPage}
                onRowsPerPageChange={setItemsPerPage}
                itemId='id'
                columns={columns}
                onRowClick={openEdit}
            />

            <Dialog
                open={draft !== null}
                onClose={close}
                fullWidth
                maxWidth='sm'
            >
                <DialogTitle>
                    {editingId ? 'Frage bearbeiten' : 'Neue Frage'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            autoFocus
                            required
                            fullWidth
                            label='Frage (DE)'
                            value={draft?.labelDe ?? ''}
                            slotProps={{
                                htmlInput: {
                                    maxLength: QUESTION_LABEL_MAX_LENGTH,
                                },
                            }}
                            helperText={`Max. ${QUESTION_LABEL_MAX_LENGTH} Zeichen.`}
                            onChange={(e) =>
                                updateDraft({ labelDe: e.target.value })
                            }
                        />
                        <TextField
                            required
                            fullWidth
                            label='Question (EN)'
                            value={draft?.labelEn ?? ''}
                            slotProps={{
                                htmlInput: {
                                    maxLength: QUESTION_LABEL_MAX_LENGTH,
                                },
                            }}
                            helperText={`Max. ${QUESTION_LABEL_MAX_LENGTH} Zeichen.`}
                            onChange={(e) =>
                                updateDraft({ labelEn: e.target.value })
                            }
                        />
                        <TextField
                            select
                            fullWidth
                            label='Art der Frage'
                            value={draft?.funnel ?? 'info'}
                            onChange={(e) =>
                                updateDraft({
                                    funnel: e.target.value as QuestionFunnel,
                                })
                            }
                        >
                            {FUNNEL_OPTIONS.map(({ value, label }) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={draft?.requiresLogin ?? false}
                                    onChange={(e) =>
                                        updateDraft({
                                            requiresLogin: e.target.checked,
                                        })
                                    }
                                />
                            }
                            label='Nur für angemeldete Kunden'
                        />
                        <TextField
                            type='number'
                            fullWidth
                            label='Reihenfolge'
                            value={draft?.sortIndex ?? 0}
                            slotProps={{ htmlInput: { min: 0 } }}
                            onChange={(e) =>
                                updateDraft({
                                    sortIndex: Math.max(
                                        0,
                                        Number(e.target.value) || 0
                                    ),
                                })
                            }
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={draft?.enabled ?? true}
                                    onChange={(e) =>
                                        updateDraft({
                                            enabled: e.target.checked,
                                        })
                                    }
                                />
                            }
                            label='Aktiv'
                        />

                        {saveError && (
                            <Alert severity='error'>
                                {errorStatus(saveError) === 400
                                    ? 'Speichern fehlgeschlagen: eine Frage überschreitet die erlaubte Länge oder die Art ist ungültig.'
                                    : 'Speichern fehlgeschlagen.'}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>Abbrechen</Button>
                    {editingId && (
                        <Button
                            color='error'
                            onClick={() => setConfirmingDelete(true)}
                        >
                            Löschen
                        </Button>
                    )}
                    <LoadingButton
                        variant='contained'
                        loading={isSaving}
                        disabled={
                            !draft?.labelDe.trim() || !draft?.labelEn.trim()
                        }
                        onClick={() => void submit()}
                    >
                        Speichern
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmingDelete}
                onClose={() => setConfirmingDelete(false)}
            >
                <DialogTitle>Frage löschen</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Diese Frage wird sofort für alle Kunden im Chat entfernt
                        und kann nicht wiederhergestellt werden. Soll die Frage{' '}
                        <Typography sx={{ fontWeight: 600 }} component='span'>
                            {draft?.labelDe}
                        </Typography>{' '}
                        wirklich gelöscht werden?
                    </DialogContentText>
                    {deleteError && (
                        <Alert severity='error' sx={{ mt: 2 }}>
                            Löschen fehlgeschlagen.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmingDelete(false)}>
                        Abbrechen
                    </Button>
                    <LoadingButton
                        color='error'
                        variant='contained'
                        loading={isDeleting}
                        onClick={() => void confirmDelete()}
                    >
                        Löschen
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
};
